import { useQuery } from '@tanstack/react-query'
import { ApiError, type components } from '@akillidernek/api-client'
import { Badge, Card, CardBody, CardHeader } from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { AyarlarSekmeleri } from './ayarlar-sekmeler'

type Fatura = components['schemas']['InvoiceRead']

const DURUM: Record<
  Fatura['status'],
  { etiket: string; ton: 'neutral' | 'info' | 'success' | 'danger' }
> = {
  draft: { etiket: 'Taslak', ton: 'neutral' },
  issued: { etiket: 'Ödeme bekliyor', ton: 'info' },
  paid: { etiket: 'Ödendi', ton: 'success' },
  void: { etiket: 'İptal', ton: 'danger' },
}

/** Kuruş → "1.788,00 TL". Para her yerde kuruş tutulur (ADR-3). */
function tutar(kurus: number, currency: string) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(kurus / 100)
}

const tarih = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('tr-TR') : '—'

/**
 * Ayarlar › Abonelik — YES-175.
 *
 * Derneğin platforma ödediği faturalar. Derneğin ÜYESİNE kestiği makbuzla
 * karıştırılmasın diye ayrı ekran: taraf da vergi de seri de farklı.
 */
export function AyarlarAbonelik() {
  const slug = useTenantSlug()

  const abonelik = useQuery({
    queryKey: ['subscription', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/subscription')
      return data
    },
  })

  const faturalar = useQuery({
    queryKey: ['subscription-invoices', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/subscription/invoices')
      return data ?? []
    },
  })

  const yetkisiz = faturalar.error instanceof ApiError && faturalar.error.problem.status === 403

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Ayarlar</h1>
      <AyarlarSekmeleri />

      <Card>
        <CardHeader
          title="Abonelik"
          description="Derneğinizin Akıllı Dernek planı ve ödeme dönemi."
        />
        <CardBody className="text-sm">
          {abonelik.data ? (
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              <div className="flex flex-col">
                <dt className="text-xs text-[var(--ad-text-muted)]">Plan</dt>
                <dd>{abonelik.data.subscription.plan_name ?? 'Plan yok'}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-xs text-[var(--ad-text-muted)]">Açık özellikler</dt>
                <dd>{abonelik.data.subscription.features.length}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-[var(--ad-text-muted)]">Yükleniyor…</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Faturalar"
          description="Platformun derneğinize kestiği abonelik faturaları."
        />
        <CardBody>
          {yetkisiz ? (
            <p className="text-sm">Faturaları görme yetkiniz yok.</p>
          ) : faturalar.data && faturalar.data.length === 0 ? (
            <p className="text-sm text-[var(--ad-text-muted)]">Henüz fatura yok.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--ad-border)] text-left text-xs text-[var(--ad-text-muted)]">
                    <th className="py-2 pr-3 font-medium">No</th>
                    <th className="py-2 pr-3 font-medium">Dönem</th>
                    <th className="py-2 pr-3 font-medium">Plan</th>
                    <th className="py-2 pr-3 font-medium">Durum</th>
                    <th className="py-2 pr-3 text-right font-medium">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {(faturalar.data ?? []).map((fatura) => (
                    <tr
                      key={fatura.id}
                      className="border-b border-[var(--ad-border)] last:border-0"
                    >
                      <td className="py-2 pr-3 font-mono text-xs text-[var(--ad-text-muted)]">
                        {fatura.number ?? '—'}
                      </td>
                      <td className="py-2 pr-3">
                        {tarih(fatura.period_start)} – {tarih(fatura.period_end)}
                      </td>
                      <td className="py-2 pr-3">
                        {fatura.plan_name}
                        <span className="text-xs text-[var(--ad-text-muted)]">
                          {' '}
                          · {fatura.billing_cycle === 'yearly' ? 'yıllık' : 'aylık'}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <Badge tone={DURUM[fatura.status].ton}>{DURUM[fatura.status].etiket}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {tutar(fatura.total_amount, fatura.currency)}
                        <span className="block text-xs text-[var(--ad-text-muted)]">
                          KDV {fatura.tax_rate_bp / 100}% dahil
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
